import { Badge } from "@/components/ui/badge";
import {
  getRiskGroupDescription,
  getRiskIndicatorDescription,
  mapRiskGroupName,
  mapRiskName,
  riskIndicatorGroupings,
  type RiskCategory,
  type RiskIndicator,
  type RiskIndicatorGroup,
} from "@/revenue-intelligence/lib/risk-contracts";
import { categoryIndicators, getRiskBucket, riskBadgeVariants } from "../lib/risk";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { InfoIcon, MailIcon, SparklesIcon } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { SectionCard } from "./SectionCard";
import { differenceInDays, format } from "date-fns";
import { cn } from "@/lib/utils";
import { RiskHistoryChart } from "./charts";

const now = new Date();

function showRiskGroupForCategory(group: RiskIndicatorGroup, category: RiskCategory | "all") {
  if (category === "all") return true;
  const indicatorsInGroup = riskIndicatorGroupings[group];
  const indicatorsInCategory = categoryIndicators[category];
  const atLeastOneIndicatorInGroupIsInCategory =
    indicatorsInGroup.some(indicator => indicatorsInCategory.includes(indicator));

  return atLeastOneIndicatorInGroupIsInCategory;
}

const safeFormatDate = (date: any) => {
  const d = new Date(date);
  const isInvalidFormat = isNaN(d.getTime());
  const isInvalidDate = d.getFullYear() < 2020;
  return isInvalidFormat || isInvalidDate ? "Not set" : format(d, "yyyy-MM-dd");
};

interface AtRiskDealDetailsProps {
  deal: any | undefined;
  dealEmails: any;
  riskCategory: RiskCategory | "all";
}

export function AtRiskDealDetails({
  deal,
  dealEmails,
  riskCategory,
}: AtRiskDealDetailsProps) {
  if (!deal) return null;

  const semanticAnalysis = dealEmails
    ? dealEmails?.[0]?.semantic_analysis
    : null;

  const emailContent = dealEmails
    ? dealEmails.map((de: any) => de.data)
    : null;

  const value =
    `${new Intl.NumberFormat("sv-SE").format(deal.value)} ${deal.currency_code}`
  const riskIndicators = Object.keys(deal.risks);

  const SHOW_DEBUG_INFO = false;

  const lastContactedDate = deal.provider_data?.properties?.notes_last_contacted;
  const contactedDaysAgo = lastContactedDate
    ? differenceInDays(now, lastContactedDate)
    : null;

  return (
    <div className="py-4 space-y-5">
      {/* Deal hero */}
      <SectionCard className="col-span-3">
        <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
          <div className="flex flex-col gap-1">
            <Badge
              variant={riskBadgeVariants[getRiskBucket(deal.weighted_risk_score)] as any}
              className="capitalize w-fit mb-2"
            >
              {getRiskBucket(deal.weighted_risk_score)} Risk
            </Badge>

            <h1>{deal.name}</h1>

            {/* Deal details */}
            <div className="flex flex-row flex-wrap gap-x-1.5 gap-y-3 text-sm">
              {SHOW_DEBUG_INFO && deal.provider_id && (
                <>
                  <span className="text-gray-500">
                    Provider ID
                  </span>
                  <span className="mr-5">
                    {deal.provider_id}
                  </span>
                </>
              )}

              <div className="flex flex-row gap-x-1 items-baseline">
                <span className="text-gray-500">
                  Owned by
                </span>
                <span className="shrink-0">
                  {deal.owner_name}
                </span>
              </div>

              <p className="text-[10px] text-gray-400">•</p>

              <div className="flex flex-row gap-x-1 items-baseline">
                <span className="text-gray-500 shrink-0">
                  {deal.stage_label ?? deal.provider_data.properties.dealstage}
                </span>
              </div>

              <p className="text-[10px] text-gray-400">•</p>

              {value && (
                <div className="flex flex-row gap-x-1 items-baseline">
                  <span className="text-gray-500 shrink-0">
                    {value}
                  </span>
                </div>
              )}

              {SHOW_DEBUG_INFO && (
                <>
                  <span className="text-gray-500">
                    Pipeline:
                  </span>
                  <span className="mr-5">
                    {deal.pipeline_label}
                  </span>
                </>
              )}
            </div>

            {/* Highlights */}
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <DealDetailHighlight
                title="Set to close"
                value={safeFormatDate(deal.closed_at)}
              />
              <DealDetailHighlight
                title="Expected to close"
                value={safeFormatDate(deal.adjustedCloseDate)}
              />
              {contactedDaysAgo && (
                <DealDetailHighlight
                  title="Days since contact"
                  value={`${contactedDaysAgo}d`}
                />
              )}
              <DealDetailHighlight
                title="Threading"
                value="Coming soon"
                italicized
              />
            </div>
          </div>
        </div>
      </SectionCard>

      <NextMoveCard
        text={Object
          .entries(deal.risks)
          // @ts-ignore
          .toSorted(([indicatorA, riskA], [indicatorB, riskB]) => (riskB.risk ?? 0) - (riskA.risk ?? 0))
          .slice(0, 1)
          // @ts-ignore
          .filter(([indicator, risk]) => !!risk.risk)
          .map(([_indicator, risk]) => risk)
          ?.[0]
          // @ts-ignore
          ?.suggested_next_move ?? ""
        }
      />

      {/* Top risks */}
      <SectionCard className="col-span-3">
        <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
          <div className="flex flex-col gap-8">
            {/* Header */}
            <div>
              <p className="font-medium text-sm">
                Risk factors breakdown
              </p>
              <p className="text-xs text-gray-500">
                What is driving the risk score, ranked by contribution
              </p>
            </div>

            {/* Top risks */}
            <div className="flex flex-col gap-y-6">
              {Object
                .entries(deal.risks)
                // @ts-ignore
                .toSorted(([indicatorA, riskA], [indicatorB, riskB]) => (riskB.risk ?? 0) - (riskA.risk ?? 0))
                .slice(0, 4)
                // @ts-ignore
                .filter(([indicator, risk]) => !!risk.risk)
                .map(([indicator, risk]) => (
                  <div key={indicator} className="flex flex-col gap-y-1.5">
                    <div className="flex flex-row justify-between items-baseline">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm">
                          {mapRiskName(indicator as RiskIndicator)}
                        </p>
                        {getRiskIndicatorDescription(indicator as RiskIndicator) && (
                          <Popover>
                            <PopoverTrigger className="inline-flex text-gray-400 hover:text-gray-700">
                              <InfoIcon size="14px" />
                            </PopoverTrigger>
                            <PopoverContent align="start" className="max-w-sm">
                              <div className="space-y-3 text-sm text-gray-500">
                                {getRiskIndicatorDescription(indicator as RiskIndicator)
                                  ?.split("\n\n")
                                  .map((paragraph) => (
                                    <p key={paragraph}>{paragraph}</p>
                                  ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {/* @ts-ignore */}
                        {risk.risk ?? 0} pts
                      </p>
                    </div>
                    <Progress
                      // @ts-ignore
                      value={risk.risk ?? 0}
                      className="h-2 rounded-[3px]"
                      // @ts-ignore
                      indicatorClassName={(risk.risk ?? 0) >= 50 ? "bg-red-600" : (risk.risk ?? 0) < 25 ? "bg-green-700" : ""}
                    />
                    <p className="text-gray-500 text-xs">
                      {deal.risks[indicator].risk_explanation}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard className="col-span-3">
        <div className="flex flex-col gap-y-4">
          <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
            <RiskHistoryChart history={deal.riskHistory ?? []} />
          </div>
        </div>
      </SectionCard>

      {/* Risk full details */}
      {false && (
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-5">
          {Object.keys(riskIndicatorGroupings).map(riskGroup => {
            if (!showRiskGroupForCategory(riskGroup as RiskIndicatorGroup, riskCategory)) return null;
            return (
              <div>
                <div className="bg-white shadow-sm space-y-10 border border-gray-300 px-4 py-5 rounded-[10px] h-full flex-1">
                  <div>
                    <h3>
                      {mapRiskGroupName(riskGroup as RiskIndicatorGroup)}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {getRiskGroupDescription(riskGroup as RiskIndicatorGroup)}
                    </p>
                  </div>
                  <div className="space-y-6">
                    {riskIndicators
                      .filter(indicator => riskIndicatorGroupings[riskGroup as RiskIndicatorGroup].includes(indicator as RiskIndicator))
                      .map(indicator => (
                        <div key={indicator} className="text-sm flex flex-col gap-y-0.5">
                          <div className="flex flex-row justify-between items-center">
                            <div className="mb-1 text-sm">
                              {mapRiskName(indicator as RiskIndicator)}
                            </div>
                            <div className="flex flex-row items-center gap-2">
                              {indicator === "toneOfVoice" && dealEmails && (
                                <Dialog>
                                  <DialogTrigger>
                                    <MailIcon size="14px" />
                                  </DialogTrigger>
                                  <DialogContent className="max-w-6xl">
                                    <DialogHeader className="pb-4">
                                      <DialogTitle>
                                        Email Semantic Analysis
                                      </DialogTitle>
                                    </DialogHeader>
                                    <DialogDescription>
                                      <div className="grid grid-cols-2 gap-8">
                                        <div className="flex flex-col gap-2">
                                          <p className="font-medium">
                                            Analysis Results
                                          </p>
                                          <div className="border border-gray-200 max-h-[500px] overflow-y-scroll">
                                            <pre className="text-[#1e1e1e]">
                                              {semanticAnalysis ? JSON.stringify(semanticAnalysis, null, 2) : null}
                                            </pre>
                                          </div>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                          <p className="font-medium">
                                            Email Thread
                                          </p>
                                          <div className="border border-gray-200 max-h-[500px] overflow-y-scroll">
                                            <pre className="text-[#1e1e1e]">
                                              {emailContent ? JSON.stringify(emailContent, null, 2) : null}
                                            </pre>
                                          </div>
                                        </div>
                                      </div>
                                    </DialogDescription>
                                  </DialogContent>
                                </Dialog>
                              )}
                              <Popover>
                                <PopoverTrigger>
                                  <InfoIcon size="14px" />
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                  <div className="text-sm flex flex-col gap-y-4">
                                    {/* Description */}
                                    {getRiskIndicatorDescription(indicator as RiskIndicator) && (
                                      <div className="space-y-3 text-gray-500">
                                        {getRiskIndicatorDescription(indicator as RiskIndicator)
                                          ?.split("\n\n")
                                          .map((paragraph) => (
                                            <p key={paragraph}>{paragraph}</p>
                                          ))}
                                      </div>
                                    )}

                                    {/* Score and details */}
                                    <div className="py-0.5">
                                      <div className="font-medium">
                                        <span className="mr-1">
                                          Risk:
                                        </span>
                                        <span>
                                          {deal.risks[indicator].risk} / 100
                                        </span>
                                      </div>
                                      <span className="text-gray-500">
                                        {deal.risks[indicator].risk_explanation}
                                      </span>
                                    </div>

                                    {/* DEBUG: RISK SCORE */}
                                    {/* <div> */}
                                    {/*   <div className="font-medium"> */}
                                    {/*     <span className="mr-1"> */}
                                    {/*       Risk score: */}
                                    {/*     </span> */}
                                    {/*     <span> */}
                                    {/*       {deal.risks[indicator].score} */}
                                    {/*     </span> */}
                                    {/*   </div> */}
                                    {/*   <span className="text-gray-500"> */}
                                    {/*     {deal.risks[indicator].risk_explanation} */}
                                    {/*   </span> */}
                                    {/* </div> */}

                                    {/* DEBUG: WEIGHT */}
                                    {/* <div> */}
                                    {/*   <div className="font-medium"> */}
                                    {/*     <span className="mr-1"> */}
                                    {/*       Weight: */}
                                    {/*     </span> */}
                                    {/*     <span> */}
                                    {/*       {deal.risks[indicator].weight} */}
                                    {/*     </span> */}
                                    {/*   </div> */}
                                    {/*   <span className="text-gray-500"> */}
                                    {/*     {deal.risks[indicator].isML ? "Machine learning model" : "Manually assigned"} */}
                                    {/*   </span> */}
                                    {/* </div> */}

                                    {/* DEBUG: DEAL SCORE */}
                                    {/* <div> */}
                                    {/*   <div className="font-medium"> */}
                                    {/*     <span className="mr-1"> */}
                                    {/*       Deal score: */}
                                    {/*     </span> */}
                                    {/*     <span> */}
                                    {/*       {deal.percentage_of_total_value} */}
                                    {/*     </span> */}
                                    {/*   </div> */}
                                    {/*   <div className="text-gray-500"> */}
                                    {/*     <div> */}
                                    {/*       Percentage of total open deals = {deal.deal_scale}% */}
                                    {/*     </div> */}
                                    {/*     <div> */}
                                    {/*       Stage scale = {deal.stage_scale} */}
                                    {/*     </div> */}
                                    {/*   </div> */}
                                    {/* </div> */}

                                    {/* DEBUG: TOTAL */}
                                    {/* <div className="py-0.5"> */}
                                    {/*   <div className="font-medium"> */}
                                    {/*     <span className="mr-1"> */}
                                    {/*       Total risk: */}
                                    {/*     </span> */}
                                    {/*     <span> */}
                                    {/*       {deal.risks[indicator].risk} */}
                                    {/*     </span> */}
                                    {/*   </div> */}
                                    {/*   <span className="text-gray-500"> */}
                                    {/*     {deal.risks[indicator].risk_calculation} */}
                                    {/*   </span> */}
                                    {/* </div> */}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                          <Progress
                            value={deal.risks[indicator].risk}
                            className="h-2 rounded-[3px]"
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  );
}

interface DealDetailHighlightProps {
  title: string;
  value: string;
  italicized?: boolean;
}

function DealDetailHighlight({ title, value, italicized = false }: DealDetailHighlightProps) {
  return (
    <div className="border border-gray-200 rounded-[10px] px-4 py-2.5 flex flex-col gap-1.5 bg-gray-50">
      <p className="text-xs uppercase text-gray-500 pt-0.5">
        {title}
      </p>
      <p className={cn(italicized && "italic")}>
        {value}
      </p>
    </div>
  );
}

interface NextMoveCardProps {
  text: string;
}

export function NextMoveCard({ text }: NextMoveCardProps) {
  return (
    <div className="border border-gray-300 bg-white rounded-[10px] p-4 shadow-sm">
      <div className="flex flex-row items-start gap-x-4">
        {/* Sparkles */}
        <div className="shrink-0 h-10 w-10 rounded-[10px] border border-[#5E548E]/50 bg-[#5E548E]/20 flex justify-center items-center">
          <SparklesIcon
            strokeWidth={1.5}
            className="text-[#5E548E]"
          />
        </div>
        {/* Copy */}
        <div className="flex flex-col gap-y-1">
          <p className="font-semibold uppercase text-xs text-gray-500">
            Recommended next move
          </p>
          <p className="text-sm">
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}
